import { useRef, useCallback, useState } from "preact/hooks"
import type { InGridConfig } from "@/utils/ingridConfig"
import style from "./InGridTile.module.css"

interface InGridTileProps {
  config: InGridConfig
}

const TILE_CLASS_MAP: Record<number, string> = {
  1: "content-card--1-tile",
  2: "content-card--2-tile",
  4: "content-card--4-tile"
}

export default function InGridTile({ config }: InGridTileProps) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [paused, setPaused] = useState(false)

  const toggleVideo = useCallback(() => {
    const video = videoRef.current
    if (!video) return
    if (video.paused) {
      video.play()
      setPaused(false)
    } else {
      video.pause()
      setPaused(true)
    }
  }, [])

  const tileModifier = TILE_CLASS_MAP[config.columnSpan] ?? TILE_CLASS_MAP[1]
  const mobileImage = config.mobileImage || config.desktopImage
  const mediaClass = config.columnSpan !== 4 ? style.media : undefined
  // Fall back to the primary CTA so the media is clickable even when the
  // customizer's dedicated "image link" slot is empty — merchants rarely fill
  // both and expect a click on the image to follow the same destination as
  // the CTA below it.
  const mediaLink = config.imageLink || config.ctaUrl || config.ctaUrl2
  const mediaLabel = config.heading || config.ctaText || config.ctaText2 || "View content"

  // LCP fix: `position` is 1-based (tile renders before that product
  // ordinal), so low-position tiles sit in the above-fold rows of the grid. A
  // lazy image there can become the page's lazy-loaded LCP element. Eager-load
  // early tiles; a tile at the very top of the grid is the dominant LCP
  // candidate and also gets the high-priority hint.
  const eagerTile = config.position <= 2
  const imageLoadingProps = eagerTile
    ? ({ loading: "eager", fetchpriority: config.position === 1 ? "high" : undefined } as const)
    : ({ loading: "lazy" } as const)

  return (
    <div className={style.tile} data-column-span={config.columnSpan}>
      <div className={`content-card ${tileModifier}`}>
        {config.mediaType === "image" && config.desktopImage && (
          <div className={`content-card__image ${mediaClass || ""}`}>
            <picture>
              <source media="(max-width: 768px)" srcSet={`${mobileImage} 768w`} />
              <source media="(min-width: 769px)" srcSet={`${config.desktopImage} 2200w`} />
              <img
                className="content-card__img-responsive"
                src={mobileImage}
                alt={config.heading || ""}
                {...imageLoadingProps}
              />
            </picture>
            {mediaLink && (
              <a className="content-card__image-link" href={mediaLink} aria-label={mediaLabel}>
                <span className="sr-only">{mediaLabel}</span>
              </a>
            )}
          </div>
        )}

        {config.mediaType === "video" && (config.videoFile || config.videoUrl) && (
          <div className={`content-card__video ${mediaClass || ""}`}>
            {config.videoSource === "shopify" && config.videoFile ? (
              <video
                ref={videoRef}
                src={config.videoFile}
                autoPlay
                loop
                muted
                playsInline
                className="content-card__img-responsive"
              />
            ) : (
              <iframe
                src={config.videoUrl}
                title={config.heading || "Video"}
                allow="autoplay; fullscreen"
                style={{ width: "100%", height: "100%", border: "none" }}
              />
            )}
            {config.videoSource === "shopify" && config.videoFile && (
              <button
                className={`content-card__video-toggle ${paused ? "is-play" : "is-pause"}`}
                aria-label={paused ? "Play background video" : "Pause background video"}
                aria-pressed={paused}
                onClick={toggleVideo}
              >
                <span className="sr-only">{paused ? "Play background video" : "Pause background video"}</span>
                <span className="icon" />
              </button>
            )}
          </div>
        )}

        <div className="content-card__content">
          {config.eyebrow && <p className="content-card__eyebrow accent-medium">{config.eyebrow}</p>}
          {config.heading && <h2 className="content-card__heading heading-medium">{config.heading}</h2>}
          <div className="content-card__links">
            {config.ctaText && config.ctaUrl && (
              <a className="content-card__link body-small" href={config.ctaUrl}>
                {config.ctaText}
              </a>
            )}
            {config.ctaText2 && config.ctaUrl2 && (
              <a className="content-card__link body-small" href={config.ctaUrl2}>
                {config.ctaText2}
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
